import { ArrowBackIosNew, ArrowForwardIos, CalendarMonth } from "@mui/icons-material";
import { Avatar, Box, Button, IconButton, Tooltip, Typography } from "@mui/material";
import { DateTime } from "luxon";
import { useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom"; // <-- CAMBIO: Importamos useNavigate
import { socket } from "../../../../sockets";
import { PaginationLoadingOverlay } from "../../../clients/PaginationLoadingOverlay";
import { JobsCalendar } from "../../../jobs2/jobs-calendar/JobsCalendar";
import { JobStatusStep } from "./JobStatusStep";
import { useJobEventsPaginated } from "./hooks/useJobEventsPaginated";
import { useJobStepperNavigation } from "./hooks/useJobStepperNavigation";
import { useScopeByUid } from "./hooks/useScopeByUid";
import { colors } from "../../../ui/colors";
import { JobDetModal } from "../job-card/JobDetModal";
import { CalendarJobsCustom } from "@components/jobs2/CalendarJobsCustom";
import { useModals } from "src/context/ModalContext";
import { JobNotesCard } from "../../jobs-details/components/JobNotesCard";

interface Props {
  sxProps?: any;
  jobsAllQuery?: any;
  job?: any;
  currentEventUid?: string;
  showCalendar?: boolean;
  parentId?: any;
  disableDetailsButton?: boolean;
  focusJobChat?: boolean;
}

export const JobLayout = ({
  sxProps,
  jobsAllQuery,
  job,
  currentEventUid,
  showCalendar = true,
  parentId = null,
  disableDetailsButton = false,
  focusJobChat = false,
}: Props) => {
  const navigate = useNavigate(); // <-- CAMBIO: Inicializamos el hook de navegación
  const { openModal } = useModals();
  const jobId = parentId ?? job?.id;
  const { id: routeId } = useParams();
  const id = jobId === 0 ? routeId : jobId;
  const idStr = String(id ?? "").trim();
  const idRef = useRef(idStr);

  const {
    events,
    page,
    nextPage,
    prevPage,
    canNextPage,
    canPrevPage,
    isLoading,
    isError,
    currentEventIndex,
    setPage,
    pageSize,
    refetch: refetchJobEvents,
  } = useJobEventsPaginated(jobId);

  const { data: scopeData } = useScopeByUid({ uid: job?.scopeOfWorkUid });

  const {
    eventIndex,
    currentEvent,
    maxAccessibleStepIndex,
    displayStepIndex,
    selectedStatus,
    CurrentComponent,
    realStepIndex,
    inProgressIndex,
    nextEvent,
    prevEvent,
    handleStepClick,
    setEventIndex,
  } = useJobStepperNavigation(events, currentEventUid);

  useEffect(() => {
    if (!focusJobChat || !currentEvent) return;
    handleStepClick(inProgressIndex);
  }, [focusJobChat, currentEvent, handleStepClick, inProgressIndex]);

  const goToCurrentJobPage = () => {
    if (currentEventIndex === -1) return;

    const pageOfCurrent = Math.floor(currentEventIndex / pageSize) + 1;

    if (pageOfCurrent === page) {
      const localIdx = events.findIndex((ev) => ev.uid === currentEventUid);
      if (localIdx !== -1) setEventIndex(localIdx);
      return;
    }

    setPage(pageOfCurrent);
  };

  const isCurrentEvent = currentEvent?.uid === currentEventUid;

  useEffect(() => {
    idRef.current = idStr;
  }, [idStr]);

  useEffect(() => {
    if (!idStr) return;
    const roomName = `room_job_${idStr}`;

    if (!socket.joinedRooms) socket.joinedRooms = new Set();
    if (socket.joinedRooms.has(roomName)) return;
    socket.joinedRooms.add(roomName);

    socket.emit("joinJobRoom", roomName);

    return () => {
      socket.emit("leaveJobRoom", roomName);
      socket.joinedRooms.delete(roomName);
    };
  }, [idStr]);

  useEffect(() => {
    const onUpdate = (jobUpdate) => {
      if (!jobUpdate) return;

      const updateId = String(jobUpdate?.id ?? jobUpdate?.jobId ?? "").trim();
      if (updateId !== idRef.current) return;

      jobsAllQuery?.refetch?.();

      refetchJobEvents?.();
    };

    socket.on("updateJobStatus", onUpdate);
  }, []);

  const dataToRender = {
    ...job,
    events: [currentEvent],
    scopeOfWork: { ...scopeData },
  };

  const jobForDetails = {
    ...job,
    id: parentId ?? job?.jobId ?? job?.id,
    scopeOfWork: scopeData ?? job?.scopeOfWork,
  };

  const visitClient = currentEvent?.client || job?.client || {};
  const clientUid = visitClient?.uid || job?.clientUid || job?.quote?.client?.uid || currentEvent?.clientUid;
  const visitCoverageArea = currentEvent?.coverageArea || job?.coverageArea || {};
  const visitServiceName =
    job?.quote?.workScopeServices?.[0]?.workService?.name ||
    scopeData?.quotes?.[0]?.workScopeServices?.[0]?.workService?.name ||
    "Cleaning Service";
  const calendarFocusJobId = currentEvent?.uid ?? currentEventUid ?? parentId ?? job?.jobId ?? job?.id;

  const openAdvancedCalendar = (event) => {
    event.stopPropagation();
    openModal(
      "Dashboard-Jobs-Calendar",
      <CalendarJobsCustom
        defaultMode="day"
        initialDateProp={currentEvent?.startDate || job?.date || job?.scheduledDate}
        initialJobIdProp={calendarFocusJobId}
        disableTimelineJobOpen
      />,
    );
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "300px",
        }}
      >
        <PaginationLoadingOverlay isVisible={isLoading} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        backgroundColor: "var(--fx-surface-base, #FFFFFF)",
        px: 1,
        pt: 2,
        pb: 0,
        borderRadius: 2,
        boxShadow: "none",
        "& *": {
          boxShadow: "none !important",
        },
        ...sxProps,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
        justifyContent: "center",
        gap: 2,
        mb: 1.5,
        width: "100%",
        px: { xs: 2, sm: 5 },
        boxSizing: "border-box",
        }}
      >
        <Box
          sx={{
            textAlign: "center",
            display: "flex",
            flexDirection: "column",
            gap: { xs: 3 },
            width: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              gap: 1.5,
              m: 1,
              justifyContent: "center",
              flexDirection: { xs: "column", sm: "column" },
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 0.75,
                alignItems: "flex-start",
                width: "100%",
                position: "relative",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: colors.gray[500],
                  fontWeight: 700,
                  fontSize: { xs: "1rem", sm: "1.15rem" },
                  letterSpacing: "0.01em",
                  whiteSpace: "nowrap",
                }}
              >
                {currentEvent?.startDate
                  ? `Visit on ${DateTime.fromISO(currentEvent.startDate).toFormat("MMMM d, yyyy")}`
                  : "Visit"}
              </Typography>

              {!disableDetailsButton && (
                <JobDetModal
                  job={jobForDetails}
                  modalKey={`JobCard-${jobForDetails?.id}`}
                  wrapTrigger={false}
                  trigger={
                    <Typography
                      role="button"
                      tabIndex={0}
                      sx={{
                        width: "fit-content",
                        textAlign: "start",
                        lineHeight: 1,
                        fontSize: 12,
                        fontWeight: 500,
                        mb: 0.5,
                        cursor: "pointer",
                        color: colors.gray[400],
                        textDecoration: "none",
                        textUnderlineOffset: "2px",
                        transition: "color 0.18s ease, text-decoration-color 0.18s ease",
                        "&:hover": {
                          color: colors.blue[600],
                          textDecoration: "underline",
                        },
                      }}
                    >
                      Job #{jobForDetails?.id}
                    </Typography>
                  }
                />
              )}

              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Avatar
                  src={visitClient.avatar}
                  sx={{
                    width: 34,
                    height: 34,
                    bgcolor: colors.orange.base,
                    color: "#fff",
                    fontSize: "0.78rem",
                    fontWeight: 700,
                  }}
                >
                  {(visitClient.firstName?.[0] || "C").toUpperCase()}
                </Avatar>

                <Box sx={{ textAlign: "left" }}>
                  <Typography sx={{ fontSize: "0.74rem", fontWeight: 600, lineHeight: 1.3 }}>
                    {visitClient.firstName} {visitClient.lastName}
                  </Typography>
                  <Typography sx={{ fontSize: "0.65rem", color: "text.secondary", lineHeight: 1.3 }}>
                    {[visitCoverageArea.city, visitCoverageArea.state].filter(Boolean).join(", ")}
                  </Typography>
                  <Typography sx={{ fontSize: "0.65rem", color: "text.secondary", lineHeight: 1.3 }}>{visitServiceName}</Typography>
                </Box>
              </Box>

              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  right: 0,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-end",
                  gap: 0.75,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <JobNotesCard
                    key={currentEvent?.uid || `job-visit-notes-${jobForDetails?.id}`}
                    jobId={jobForDetails?.id}
                    jobEventUid={currentEvent?.uid}
                    clientUid={clientUid}
                    defaultNoteType="visit"
                    variant="popover"
                    popoverTitle="Visit Notes"
                    placeholder="Type a note for this visit..."
                    emptyMessage="No notes added for this visit yet."
                  />

                  {showCalendar && (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<CalendarMonth sx={{ fontSize: "16px !important" }} />}
                      onClick={openAdvancedCalendar}
                      sx={{
                        textTransform: "none",
                        borderRadius: 1.5,
                        py: "3px",
                        px: 1.25,
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        borderColor: colors.orange.base,
                        color: colors.orange.base,
                        "&:hover": {
                          borderColor: colors.orange.base,
                          backgroundColor: colors.orange[50],
                        },
                      }}
                    >
                      View in Calendar
                    </Button>
                  )}
                </Box>

                {!isCurrentEvent && (
                  <Button
                    variant="text"
                    size="small"
                    onClick={goToCurrentJobPage}
                    sx={{
                      textTransform: "none",
                      borderRadius: 1.5,
                      py: "2px",
                      px: 1,
                      fontSize: "0.72rem",
                      fontWeight: 700,
                      color: colors.orange.base,
                      "&:hover": {
                        backgroundColor: colors.orange[50],
                      },
                    }}
                  >
                    Return to current visit
                  </Button>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mt: 2,
          px: 0,
          py: 1,
        }}
      >
        <Tooltip title="Go to previous visit" arrow>
          <span>
            <IconButton
              onClick={() => {
                if (eventIndex > 0) {
                  prevEvent();
                } else if (canPrevPage) {
                  prevPage();
                }
              }}
              disabled={eventIndex === 0 && !canPrevPage}
              sx={{
                color: eventIndex > 0 ? "var(--fx-surface-textsecondary, #64748B)" : "gray",
                ml: -1,
              }}
            >
              <ArrowBackIosNew fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 0.75,
            flex: 1,
            minWidth: 0,
          }}
        >
          <JobStatusStep
            displayStepIndex={displayStepIndex}
            realStepIndex={realStepIndex}
            maxAccessibleStepIndex={maxAccessibleStepIndex}
            handleStepClick={handleStepClick}
          />

        </Box>

        <Tooltip title="Go to next visit" arrow>
          <span>
            <IconButton
              onClick={() => {
                if (eventIndex < events.length - 1) {
                  nextEvent();
                } else if (canNextPage) {
                  nextPage();
                }
              }}
              disabled={eventIndex === events.length - 1 && !canNextPage}
              sx={{
                color: eventIndex < events.length - 1 ? "var(--fx-surface-textsecondary, #64748B)" : "gray",
                mr: -1,
              }}
            >
              <ArrowForwardIos fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "row", sm: "column" },
        }}
      >
        {CurrentComponent && (
          <CurrentComponent
            data={dataToRender}
            focusJobChat={focusJobChat}
            currentWf={currentEvent?.Workforces ?? currentEvent?.workforces ?? []}
            jobEventUid={currentEvent?.uid}
            mode="view"
            onSaveWorkforce={async () => {
              await refetchJobEvents?.();
              await jobsAllQuery?.refetch?.();
            }}
          />
        )}
      </Box>
    </Box>
  );
};
